import { ApexOptions } from 'apexcharts';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { ComponentType, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { getBlueChartColorsSet } from '@/modules/common/utils/getBlueChartColorsSet.ts';
import { Spinner } from 'ui';
import { useWindowWidth } from 'ui/hooks';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<ReactApexChartProps>;

interface CustomPieChartProps {
  options?: ApexOptions;
  series: ApexNonAxisChartSeries;
  labels: string[];
  metricName?: string;
}

const truncateMetricName = (name: string, maxLength = 18): string => {
  if (name.length > maxLength) {
    return `${name.slice(0, maxLength)}...`;
  }
  return name;
};

const CustomPieChart = ({ series, options = {}, labels, metricName }: CustomPieChartProps) => {
  const windowWidth = useWindowWidth() || 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperRefWidth, setWrapperWidth] = useState(0);

  const filteredSeries = useMemo<number[]>(() => {
    return (series as unknown[]).filter((s): s is number => typeof s === 'number');
  }, [series]);

  const chartDonutWidth = useMemo(
    () => (windowWidth <= 768 ? wrapperRefWidth : wrapperRefWidth / 2.7),
    [windowWidth, wrapperRefWidth],
  );

  const chartHeight = useMemo(() => {
    return windowWidth <= 768 ? chartDonutWidth + (series?.length || 0) * 26 : chartDonutWidth;
  }, [chartDonutWidth, series?.length, windowWidth]);

  const handleSetWrapperWidth = useCallback(() => {
    if (!wrapperRef.current) return;
    setWrapperWidth(wrapperRef.current.getBoundingClientRect().width);
  }, []);

  useEffect(() => {
    handleSetWrapperWidth();
    window.addEventListener('resize', handleSetWrapperWidth);
    return () => {
      window.removeEventListener('resize', handleSetWrapperWidth);
    };
  }, [handleSetWrapperWidth, series]);

  const baseOptions = useMemo<ApexOptions>(() => {
    const truncatedMetricName = truncateMetricName(metricName || 'Total');
    return {
      labels,
      responsive: [
        {
          breakpoint: 768,
          options: {
            width: chartDonutWidth,
            legend: {
              width: chartDonutWidth,
              floating: false,
              position: 'bottom',
              offsetX: 0,
              offsetY: 0,
              markers: { offsetY: 0 },
            },
          },
        },
      ],
      legend: {
        position: 'right',
        width: wrapperRefWidth / 2,
        itemMargin: { vertical: 6, horizontal: 0 },
        markers: {
          width: 8,
          height: 8,
          offsetX: -5,
          offsetY: 2,
        },
        formatter: (label: string, { seriesIndex, w }) => {
          const value = w.globals.series[seriesIndex];
          return renderToString(
            <div className="flex w-[150px]">
              <div className="overflow-hidden text-nowrap text-ellipsis">{label}</div>
              <div className="font-semibold ml-auto pl-4">{value}</div>
            </div>,
          );
        },
      },
      colors: getBlueChartColorsSet(series?.length || 0),
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          donut: {
            size: '75%',
            labels: {
              show: true,
              name: { formatter: () => truncatedMetricName },
              value: { offsetY: 8, fontSize: '24px', fontWeight: 'bold' },
              total: {
                showAlways: true,
                show: true,
                label: truncatedMetricName,
                fontSize: '14px',
                color: '#737373',
                formatter: w => {
                  return w.globals.seriesTotals
                    .reduce((a: number, b: number) => a + b, 0)
                    .toLocaleString();
                },
              },
            },
          },
        },
      },
      tooltip: {
        shared: false,
        y: {
          formatter: val => val.toLocaleString(),
        },
      },
      noData: {
        text: 'No data',
      },
      stroke: {
        show: filteredSeries.length > 1,
        width: 2,
        colors: ['#fff'],
      },
    };
  }, [labels, chartDonutWidth, wrapperRefWidth, series, filteredSeries.length, metricName]);

  const resolveOptions = useMemo(
    () => merge(cloneDeep(baseOptions), options),
    [baseOptions, options],
  );

  return (
    <div
      key={`chart_${wrapperRef.current?.getBoundingClientRect()?.width}_${series?.toString()}`}
      ref={wrapperRef}
      className="flex justify-start [&>div>div>svg]:!overflow-visible [&>div>div>svg>foreignObject]:!overflow-visible [&_.apexcharts-legend>.apexcharts-legend-series]:flex  [&_.apexcharts-legend>.apexcharts-legend-series]:w-full [&_.apexcharts-legend-text]:w-full"
    >
      <Suspense
        fallback={
          <div className="min-h-[250px] h-full w-full flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        }
      >
        <Chart
          type="donut"
          series={filteredSeries}
          options={resolveOptions}
          width={wrapperRefWidth}
          height={chartHeight}
        />
      </Suspense>
    </div>
  );
};

export default CustomPieChart;
